// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xPartNumberStyle } from "@keller/App/SharedStyle/ProductPartNumberStyle";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = {
    product: ProductModel;
};

export interface ProductDetailsHeaderContentStyles {
    container?: InjectableCss;
    imgSection?: InjectableCss;
    detailsSection?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    productParts?: InjectableCss;
    partContainer?: InjectableCss;
    partName?: TypographyPresentationProps;
    partValue?: TypographyPresentationProps;
}

export const productDetailsHeaderContentStyles: ProductDetailsHeaderContentStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },

    imgSection: {
        css: css`
            flex: 0 0 56px;
            margin-right: 20px;
            > div[data-test-selector="primary-image-container"] {
                > div {
                    min-height: auto !important; // important in case of sriv
                }
                > button[data-test-selector="productDetails-image-zoom"] {
                    display: none;
                }
            }
        `,
    },
    detailsSection: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    productTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 8px;
        `,
    },
    productParts: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    partContainer: {
        css: css`
            margin-right: 20px;
            &:last-child {
                margin: 0;
            }
        `,
    },
    partName: { ...xPartNumberStyle.label },
    partValue: { ...xPartNumberStyle.value },
};

const styles = productDetailsHeaderContentStyles;

const renderPart = (label: string, value?: string) => {
    return (
        <StyledWrapper {...styles.partContainer}>
            <Typography {...styles.partName}>{label}</Typography>
            <Typography {...styles.partValue}>{value || "#"}</Typography>
        </StyledWrapper>
    );
};

const ProductDetailsSummary: FC<Props> = ({ product }) => {
    if (!product) {
        return null;
    }
    const { brand, productTitle, customerProductNumber, manufacturerItem, productNumber, properties, isVariantParent } =
        product;
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.imgSection}>
                {createWidgetElement("ProductDetails/ProductDetailsPrimaryImage", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.detailsSection}>
                {productTitle && <Typography {...styles.productTitle}>{productTitle}</Typography>}
                <StyledWrapper {...styles.productParts}>
                    {renderPart(translate("Brand"), brand?.name)}
                    {renderPart(translate("MFG #"), manufacturerItem)}
                    {!isVariantParent && renderPart(translate("Keller #"), productNumber)}
                    {renderPart(translate("My Part #"), customerProductNumber)}
                    {renderPart(translate("Last Ordered"), properties.lastOrderedDate)}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductDetailsSummary;
